import { useState } from "react";
import { Table, Tag, Input, Card, Progress } from "antd";
import { Search, Rocket } from "lucide-react";
import { useOnboardingOverview } from "../../../api-hooks/companies";
import dayjs from "dayjs";

const statusColor: Record<string, string> = {
  active: "green", trial: "blue", past_due: "orange", cancelled: "red", paused: "default",
};

export default function OnboardingOverviewPage() {
  const [search, setSearch] = useState("");
  const { data: companies = [], isLoading } = useOnboardingOverview();

  const filtered = companies.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return c.company_name?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q);
  });

  const incomplete = companies.filter((c) => !c.is_complete).length;
  const notStarted = companies.filter((c) => !c.started).length;

  const columns = [
    {
      title: "Company",
      key: "company",
      render: (_: any, r: any) => (
        <div>
          <div className="font-medium text-slate-800">{r.company_name}</div>
          <div className="text-xs text-slate-500">{r.email}</div>
        </div>
      ),
    },
    {
      title: "Subscription",
      dataIndex: "subscription_status",
      key: "subscription_status",
      render: (s: string) => s
        ? <Tag color={statusColor[s] || "default"}>{s.replace("_", " ").toUpperCase()}</Tag>
        : <span className="text-slate-400">—</span>,
    },
    {
      title: "Onboarding Progress",
      key: "progress",
      width: 200,
      // The SetupWizard reports no per-step progress to the backend — it's a
      // single "founding admin completed the wizard" flag — so this can only
      // honestly render a binary complete/incomplete state, not a fabricated
      // step count.
      render: (_: any, r: any) => {
        if (!r.started) {
          return <span className="text-slate-400 text-xs">Not started</span>;
        }
        const pct = r.progress_pct;
        const strokeColor = pct === 100 ? "#16a34a" : "#dc2626";
        return (
          <Progress
            percent={pct}
            size="small"
            style={{ width: 140, margin: 0 }}
            strokeColor={strokeColor}
            format={(p) => <span className="text-xs">{p}%</span>}
          />
        );
      },
      sorter: (a: any, b: any) => (a.progress_pct ?? 0) - (b.progress_pct ?? 0),
    },
    {
      title: "Status",
      key: "status",
      render: (_: any, r: any) => {
        if (r.is_complete) return <Tag color="green">Complete</Tag>;
        if (!r.started) return <Tag color="default">Not Started</Tag>;
        return <Tag color="orange">In Progress</Tag>;
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created_at",
      render: (v: string) => dayjs(v).format("DD MMM YYYY"),
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Rocket size={20} className="text-violet-700" />
            <h2 className="text-xl font-semibold text-slate-800">Company Onboarding Progress</h2>
          </div>
          <p className="text-slate-500 text-sm">Onboarding completion across all companies</p>
        </div>
        <div className="flex gap-3">
          {incomplete > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl px-3 py-2">
              <span className="text-orange-600 font-semibold text-sm">{incomplete} Incomplete</span>
            </div>
          )}
          {notStarted > 0 && (
            <div className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">
              <span className="text-slate-600 font-semibold text-sm">{notStarted} Not Started</span>
            </div>
          )}
        </div>
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm mb-4">
        <Input
          prefix={<Search size={14} className="text-slate-400" />}
          placeholder="Search by company name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg"
          style={{ maxWidth: 360 }}
          allowClear
        />
      </Card>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="company_id"
          loading={isLoading}
          size="small"
          pagination={{ pageSize: 30, showTotal: (t) => `${t} companies` }}
          scroll={{ x: "max-content" }}
          rowClassName={(r) => {
            if (!r.started) return "opacity-60";
            if (!r.is_complete && r.progress_pct < 30) return "bg-red-50/30";
            return "";
          }}
        />
      </Card>
    </div>
  );
}
